import { useState } from "react";
import { Link } from "react-router-dom";
import {
  deleteCloudPracticePlan,
  saveCloudPracticePlan,
} from "../lib/cloud-practice-storage";
import {
  deletePracticeTemplate,
  loadPracticeTemplates,
  practiceDurationMinutes,
  savePracticeTemplate,
  type PracticePlan,
} from "../lib/practice-storage";

const displayDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || "No date";
};

export function PracticeTemplatesRoute() {
  const [templates, setTemplates] = useState(loadPracticeTemplates);

  const duplicate = (template: PracticePlan) => {
    const result = savePracticeTemplate({
      ...template,
      id: "current-practice",
      team: `${template.team || "Practice"} copy`,
    });
    setTemplates(result.templates);
    void saveCloudPracticePlan(result.template);
  };

  const remove = (template: PracticePlan) => {
    if (!window.confirm(`Delete “${template.team || "Practice template"}”?`))
      return;
    setTemplates(deletePracticeTemplate(template.id));
    void deleteCloudPracticePlan(template.id);
  };

  return (
    <main className="template-library-page">
      <section className="template-library-hero">
        <div>
          <p className="eyebrow">Practice templates</p>
          <h1>
            Plan once.
            <br />
            <span>Coach faster.</span>
          </h1>
          <p>
            Save complete practices, reuse them with a fresh date, and adjust
            the stations for your next ice time.
          </p>
        </div>
        <Link className="primary-button large" to="/practice">
          Create a practice
        </Link>
      </section>

      <section className="template-library-content">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Your library</p>
            <h2>
              {templates.length} saved{" "}
              {templates.length === 1 ? "template" : "templates"}
            </h2>
          </div>
        </div>
        {templates.length ? (
          <div className="practice-template-grid">
            {templates.map((template) => {
              const stationMinutes = template.stations.reduce(
                (total, station) => total + station.durationMinutes,
                0,
              );
              const minutes = practiceDurationMinutes(
                template.startTime,
                template.endTime,
                stationMinutes,
              );
              return (
                <article className="practice-template-card" key={template.id}>
                  <div className="practice-template-card-head">
                    <div>
                      <span>{template.ageGroup}</span>
                      <span>{minutes} min</span>
                      <span>{displayDate(template.date)}</span>
                    </div>
                    <h3>{template.team || "Untitled practice"}</h3>
                    <p>{template.theme || "No practice theme added."}</p>
                  </div>
                  <ol>
                    {template.stations.map((station, index) => (
                      <li key={`${template.id}-${index}`}>
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <strong>
                          {station.title || `Station ${index + 1}`}
                        </strong>
                        <small>{station.durationMinutes} min</small>
                      </li>
                    ))}
                  </ol>
                  <div className="practice-template-actions">
                    <Link
                      className="primary-button"
                      to={`/practice?template=${encodeURIComponent(template.id)}`}
                    >
                      Edit template
                    </Link>
                    <button onClick={() => duplicate(template)}>
                      Duplicate
                    </button>
                    <button className="danger" onClick={() => remove(template)}>
                      Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="empty-template-library">
            <span aria-hidden="true">▦</span>
            <h3>No practice templates yet</h3>
            <p>
              Open the practice planner, build your practice, then choose “Save
              as template.”
            </p>
            <Link className="primary-button" to="/practice">
              Build your first template
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
